import { LeaderboardPlayer, GameHistoryItem } from './types';
import { Faction } from '../../types/stats';

export interface RawPlayerRoundRecord {
  id?: number;
  game_record_id?: number | null;
  player_number: number;
  player_nickname?: string | null;
  player_role?: string | null;
  faction?: string;
  score_change?: number | null;
  is_mvp?: boolean | null;
  game_records?: {
    victory_type?: string | null;
    task_results?: unknown;
  } | null;
}

export interface AggregatedPlayerStats {
  player_number: number;
  player_name: string;
  total_score: number;
  total_games: number;
  wins: number;
  losses: number;
  good_wins: number;
  good_losses: number;
  evil_wins: number;
  evil_losses: number;
  mvp_count: number;
  percival_wins: number;
  percival_games: number;
  merlin_wins: number;
  merlin_games: number;
  lancelot_good_wins: number;
  lancelot_good_games: number;
  lancelot_evil_wins: number;
  lancelot_evil_games: number;
  loyal_wins: number;
  loyal_games: number;
  mordred_wins: number;
  mordred_games: number;
  morgana_wins: number;
  morgana_games: number;
  assassin_wins: number;
  assassin_games: number;
  assassin_merlin_kills: number;
  minion_wins: number;
  minion_games: number;
  oberon_wins: number;
  oberon_games: number;
}

const createEmptyStats = (): AggregatedPlayerStats => ({
  player_number: 0,
  player_name: '',
  total_score: 0,
  total_games: 0,
  wins: 0,
  losses: 0,
  good_wins: 0,
  good_losses: 0,
  evil_wins: 0,
  evil_losses: 0,
  mvp_count: 0,
  percival_wins: 0,
  percival_games: 0,
  merlin_wins: 0,
  merlin_games: 0,
  lancelot_good_wins: 0,
  lancelot_good_games: 0,
  lancelot_evil_wins: 0,
  lancelot_evil_games: 0,
  loyal_wins: 0,
  loyal_games: 0,
  mordred_wins: 0,
  mordred_games: 0,
  morgana_wins: 0,
  morgana_games: 0,
  assassin_wins: 0,
  assassin_games: 0,
  assassin_merlin_kills: 0,
  minion_wins: 0,
  minion_games: 0,
  oberon_wins: 0,
  oberon_games: 0,
});

export function normalizePlayerName(record: RawPlayerRoundRecord): string {
  return record.player_nickname?.trim() || `${record.player_number}号`;
}

export function buildMissionSummary(taskResults?: unknown): string {
  const results = Array.isArray(taskResults) ? taskResults : [];
  const validResults = results.filter(value => value !== null);
  const successCount = validResults.filter(value => value === true).length;
  const failCount = validResults.filter(value => value === false).length;
  return `${successCount}胜${failCount}负`;
}

export function buildAssassinAttempt(victoryType?: string | null): string {
  if (victoryType === 'assassin_success') return '刺杀梅林成功';
  if (victoryType === 'assassin_fail') return '提前刺杀失败';
  return '';
}

export function aggregatePlayerStats(records: RawPlayerRoundRecord[]): Record<string, AggregatedPlayerStats> {
  const uniqueRecords = new Map<string, RawPlayerRoundRecord>();

  for (const record of records) {
    if (!record.game_record_id) continue;
    const playerName = normalizePlayerName(record);
    const key = `${playerName}_${record.game_record_id}`;
    if (!uniqueRecords.has(key)) {
      uniqueRecords.set(key, record);
    }
  }

  const statsMap: Record<string, AggregatedPlayerStats> = {};

  for (const record of uniqueRecords.values()) {
    const playerName = normalizePlayerName(record);
    if (!statsMap[playerName]) {
      statsMap[playerName] = {
        ...createEmptyStats(),
        player_number: record.player_number || 0,
        player_name: playerName,
      };
    }

    const stats = statsMap[playerName];
    const scoreChange = Number(record.score_change) || 0;
    const isWin = scoreChange > 0;
    const role = record.player_role || '';
    const faction = (record.faction || '') as Faction;

    stats.total_score += scoreChange;
    stats.total_games += 1;
    stats.wins += isWin ? 1 : 0;
    stats.losses += isWin ? 0 : 1;
    stats.good_wins += faction === 'good' && isWin ? 1 : 0;
    stats.good_losses += faction === 'good' && !isWin ? 1 : 0;
    stats.evil_wins += faction === 'evil' && isWin ? 1 : 0;
    stats.evil_losses += faction === 'evil' && !isWin ? 1 : 0;
    stats.mvp_count += record.is_mvp ? 1 : 0;

    if (role === 'percival') {
      stats.percival_games += 1;
      if (isWin && faction === 'good') stats.percival_wins += 1;
    }
    if (role === 'merlin') {
      stats.merlin_games += 1;
      if (isWin && faction === 'good') stats.merlin_wins += 1;
    }
    if (role === 'lancelot_good') {
      stats.lancelot_good_games += 1;
      if (isWin && faction === 'good') stats.lancelot_good_wins += 1;
    }
    if (role === 'lancelot_evil') {
      stats.lancelot_evil_games += 1;
      if (isWin && faction === 'evil') stats.lancelot_evil_wins += 1;
    }
    if (role === 'loyal') {
      stats.loyal_games += 1;
      if (isWin && faction === 'good') stats.loyal_wins += 1;
    }
    if (role === 'mordred') {
      stats.mordred_games += 1;
      if (isWin && faction === 'evil') stats.mordred_wins += 1;
    }
    if (role === 'morgana') {
      stats.morgana_games += 1;
      if (isWin && faction === 'evil') stats.morgana_wins += 1;
    }
    if (role === 'assassin') {
      stats.assassin_games += 1;
      if (isWin && record.game_records?.victory_type === 'assassin_success') {
        stats.assassin_wins += 1;
        stats.assassin_merlin_kills += 1;
      }
    }
    if (role === 'minion') {
      stats.minion_games += 1;
      if (isWin && faction === 'evil') stats.minion_wins += 1;
    }
    if (role === 'oberon') {
      stats.oberon_games += 1;
      if (isWin && faction === 'evil') stats.oberon_wins += 1;
    }
  }

  return statsMap;
}

export function toLeaderboardPlayers(statsMap: Record<string, AggregatedPlayerStats>): LeaderboardPlayer[] {
  return Object.values(statsMap).map(stats => {
    const totalGames = stats.total_games || 0;
    const wins = stats.wins || 0;
    const totalScore = stats.total_score || 0;

    return {
      player_number: stats.player_number || 0,
      player_name: stats.player_name || '',
      total_score: totalScore,
      total_games: totalGames,
      wins,
      losses: stats.losses || 0,
      good_wins: stats.good_wins || 0,
      good_losses: stats.good_losses || 0,
      evil_wins: stats.evil_wins || 0,
      evil_losses: stats.evil_losses || 0,
      mvp_count: stats.mvp_count || 0,
      percival_wins: stats.percival_wins || 0,
      percival_games: stats.percival_games || 0,
      merlin_wins: stats.merlin_wins || 0,
      merlin_games: stats.merlin_games || 0,
      lancelot_good_wins: stats.lancelot_good_wins || 0,
      lancelot_good_games: stats.lancelot_good_games || 0,
      lancelot_evil_wins: stats.lancelot_evil_wins || 0,
      lancelot_evil_games: stats.lancelot_evil_games || 0,
      loyal_wins: stats.loyal_wins || 0,
      loyal_games: stats.loyal_games || 0,
      mordred_wins: stats.mordred_wins || 0,
      mordred_games: stats.mordred_games || 0,
      morgana_wins: stats.morgana_wins || 0,
      morgana_games: stats.morgana_games || 0,
      assassin_wins: stats.assassin_wins || 0,
      assassin_games: stats.assassin_games || 0,
      assassin_merlin_kills: stats.assassin_merlin_kills || 0,
      minion_wins: stats.minion_wins || 0,
      minion_games: stats.minion_games || 0,
      oberon_wins: stats.oberon_wins || 0,
      oberon_games: stats.oberon_games || 0,
      win_rate: totalGames > 0 ? Math.round((wins / totalGames) * 100) : 0,
      avg_score: totalGames > 0 ? Math.round(totalScore / totalGames) : 0,
    };
  });
}

export function summarizeGameRecord(game: {
  victory_type?: string | null;
  task_results?: unknown;
}): {
  mission_summary: string;
  assassin_attempt: string;
} {
  return {
    mission_summary: buildMissionSummary(game.task_results),
    assassin_attempt: buildAssassinAttempt(game.victory_type),
  };
}

export function applyPenaltyAdjustments<T extends { player_name: string; total_score: number }>(
  playerStats: T[],
  penaltyRecords: Array<{ player_nickname?: string | null; penalty_score?: number | null }>
): number {
  const penaltyMap = new Map<string, number>();

  for (const record of penaltyRecords) {
    const playerNickname = record.player_nickname?.trim();
    if (!playerNickname) continue;

    const score = Number(record.penalty_score) || 0;
    penaltyMap.set(playerNickname, (penaltyMap.get(playerNickname) || 0) + score);
  }

  let totalPenaltyApplied = 0;
  for (const player of playerStats) {
    const penalty = penaltyMap.get(player.player_name) || 0;
    if (penalty !== 0) {
      player.total_score += penalty;
      totalPenaltyApplied += penalty;
    }
  }

  return totalPenaltyApplied;
}
